'use client';
import {
  AspectRatio,
  DialogOpenChangeDetails,
  HStack,
  Icon,
  Image,
  Link,
  Text,
  UseDisclosureReturn,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useContext, useMemo, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '~/components/ui/dialog';
import { toaster } from '~/components/ui/toaster';
import { apiClient } from '~/lib/apiClient';
import { isActiveToken } from '~/src/services/users';
import { RootContext } from '~/store';
import { PrizeTicket } from '~/types/resources/prizeTicket';
import { WebrollButton } from '../common/buttons/WebrollButton';
import { Checkbox } from '../ui/checkbox';

type Props = {
  mutate: () => void;
  availableTickets: PrizeTicket[];
  selectedTicketId: string | undefined;
  ticketUseModalDisclosure: UseDisclosureReturn;
};

export const PrizeTicketUseModal: React.FC<Props> = (props: Props) => {
  const { token, loggedInState } = useContext(RootContext);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const selectedPrizeTickets = useMemo(
    () =>
      props.availableTickets.filter(
        (ticket) => ticket.ticket_id == props.selectedTicketId
      ),
    [props.availableTickets, props.selectedTicketId]
  );
  const selectedPrizeTicket = selectedPrizeTickets[0];

  const formattedUsedAt = () => {
    if (selectedPrizeTicket.expired_at) {
      const date = new Date(selectedPrizeTicket.expired_at);
      return format(date, "yyyy年M月d日' 'HH時mm分");
    }
  };

  const handleClose = () => {
    props.ticketUseModalDisclosure.onClose();
    setIsConfirmed(false);
  };

  const handleClick = async () => {
    if (!isConfirmed || !isActiveToken(token) || loggedInState === 'loading')
      return;

    try {
      const res = await apiClient.v1.me.prize_tickets
        ._serial_code(selectedPrizeTicket.serial_code)
        .patch({
          headers: {
            Authorization: `Bearer ${token?.token}`,
          },
        });
      props.ticketUseModalDisclosure.onClose();
      setIsConfirmed(false);
      props.mutate();
      if (res.status == 200) {
        toaster.success({
          title: 'チケットを使用しました',
          duration: 9000,
        });
      } else if (res.originalResponse.status == 400) {
        toaster.error({
          title: 'チケットの使用に失敗しました。',
          description: 'このチケットはすでに使用済みです。',
          duration: 9000,
        });
      } else if (res.originalResponse.status == 404) {
        toaster.error({
          title: 'チケットの使用に失敗しました。',
          description: 'このチケットは存在しません。',
          duration: 9000,
        });
      }
    } catch (e) {
      console.error('error', e);
      toaster.error({
        title: 'チケットの使用に失敗しました。',
        description: '「メニュー」「お問い合わせ」よりご連絡ください',
        duration: 9000,
      });
    }
  };

  return (
    <DialogRoot
      open={props.ticketUseModalDisclosure.open}
      onOpenChange={(details: DialogOpenChangeDetails) => {
        if (!details.open) {
          handleClose();
        }
      }}
      scrollBehavior="inside"
      initialFocusEl={() => null}
    >
      <DialogBackdrop />
      <DialogContent mx="8px">
        <DialogHeader p="8px">
          <HStack w="full" justify="flex-end" align="end">
            <AspectRatio ratio={1}>
              <Image
                src="/assets/icon/close.svg"
                alt="icon"
                onClick={props.ticketUseModalDisclosure.onClose}
              />
            </AspectRatio>
          </HStack>
        </DialogHeader>
        <DialogBody>
          {selectedPrizeTicket != null && (
            <VStack w="full" px="8px" spaceX="10px" align="stretch">
              <Image
                src={selectedPrizeTicket.thumbnail_url}
                alt={`${selectedPrizeTicket.thumbnail_url}の画像`}
                loading="lazy"
                pointerEvents="none"
              />
              <Text fontSize="8px" textAlign="center">
                シリアルナンバー : {selectedPrizeTicket.serial_code}
              </Text>
              <Text
                fontSize="16px"
                mt="8px"
                mx="8px"
                fontWeight="bold"
                textAlign="center"
                overflowWrap="break-word"
              >
                {selectedPrizeTicket.name}
              </Text>
              <Text fontSize="14px" textAlign="center" fontWeight="bold">
                所持数 : {selectedPrizeTickets.length}個
              </Text>
              {selectedPrizeTicket.description
                .split('\\n')
                .map((text, index) => (
                  <Text
                    key={index}
                    fontSize="13px"
                    textAlign="left"
                    whiteSpace="pre-wrap"
                  >
                    {text}
                  </Text>
                ))}
              <Text fontSize="13px" textAlign="left">
                使用期限 : {formattedUsedAt() ?? '無期限'}
              </Text>
              {selectedPrizeTicket.available_place && (
                <Text fontSize="13px" textAlign="left">
                  利用可能場所 : {selectedPrizeTicket.available_place}
                </Text>
              )}
              {selectedPrizeTicket.available_url && (
                <Link
                  fontSize="14px"
                  color="primary"
                  href={selectedPrizeTicket.available_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MAP <Icon as={FaExternalLinkAlt} w={3} h={3} />
                </Link>
              )}
            </VStack>
          )}
        </DialogBody>
        <DialogFooter>
          <VStack w="full" justify="stretch">
            <Checkbox
              fontSize="14px"
              colorScheme="webroll"
              onChange={() => setIsConfirmed(!isConfirmed)}
              checked={isConfirmed}
            >
              スタッフによる確認済み
            </Checkbox>
            <WebrollButton
              h="42px"
              fontSize="16px"
              onClick={handleClick}
              disabled={!isConfirmed}
            >
              使用する
            </WebrollButton>
          </VStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
